import { useState, useEffect, useMemo, useCallback } from "react";
import { getEmployeeNameById } from "../utils/employeeNames";

/**
 * Team View Dashboard - See what everyone is working on
 * Compact list view with grouping options (Project, Employee, Work Center, Flat)
 * Click on any row to see details in the right panel
 */
export default function TeamView({ onBackToApp, userEmail }) {
  // Admin check for delete functionality
  const isAdmin = userEmail?.toLowerCase() === "mark.herman@3dcolor.com";
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());

  // View controls
  const [groupBy, setGroupBy] = useState("employee"); // project, employee, workCenter, flat
  const [workCenterFilter, setWorkCenterFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [selectedItem, setSelectedItem] = useState(null); // Selected group or session for detail panel

  // Delete ticket state
  const [deleteConfirm, setDeleteConfirm] = useState(null); // ticket ID to confirm delete
  const [deleting, setDeleting] = useState(false);

  // Auto-refresh every 10 seconds
  useEffect(() => {
    loadSessions();
    const interval = setInterval(loadSessions, 10000);
    return () => clearInterval(interval);
  }, []);

  // Update current time every second for elapsed time display
  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  const loadSessions = async () => {
    try {
      const token = sessionStorage.getItem("msal.backendToken");
      const res = await fetch("/api/reports/active-sessions", {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error("Failed to load sessions");

      const data = await res.json();
      setSessions(data.sessions || []);
      setError(null);
    } catch (err) {
      console.error("Error loading sessions:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Delete ticket handler
  const handleDeleteTicket = async (ticketId) => {
    if (!isAdmin) return;

    setDeleting(true);
    try {
      const token = sessionStorage.getItem("msal.backendToken");
      const res = await fetch(`/api/time/tickets/${ticketId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "Failed to delete ticket");
      }

      // Remove from local state
      setSessions(prev => prev.filter(s => s.id !== ticketId));
      setSelectedItem(null);
      setDeleteConfirm(null);
    } catch (err) {
      console.error("Error deleting ticket:", err);
      alert(`Failed to delete ticket: ${err.message}`);
    } finally {
      setDeleting(false);
    }
  };

  // Work center groups (for filtering) - ordered for button display
  const workCenterGroupsOrdered = [
    { key: "all", label: "All", workCenters: null },
    { key: "PA", label: "Production Art", workCenters: ["PA", "PRINTING"] },
    { key: "ASSEMBLY+OPTI", label: "Assembly + Opti", workCenters: ["ASSEMBLY", "OPTI"] },
    { key: "3DMODEL+PAINT", label: "3D Model + Paint", workCenters: ["3D MODEL", "PAINT"] },
    { key: "PM", label: "Project Mgmt", workCenters: ["PM"] },
  ];

  // Legacy map for filtering logic
  const workCenterGroups = {
    "ASSEMBLY+OPTI": ["ASSEMBLY", "OPTI"],
    "3DMODEL+PAINT": ["3D MODEL", "PAINT"],
    "PA": ["PA", "PRINTING"],
    "PM": ["PM"],
    "FULFILLMNT": ["FULFILLMNT"],
    "QA": ["QA"],
  };

  const getGroupDisplayName = (group) => {
    const names = {
      "ASSEMBLY+OPTI": "Assembly + Opti",
      "3DMODEL+PAINT": "3D Model + Paint",
      "PA": "Production Art",
      "PM": "Project Mgmt",
      "FULFILLMNT": "Fulfillment",
      "QA": "Quality Assurance",
    };
    return names[group] || group;
  };

  // Get count for a work center group
  const getGroupCount = (workCenters) => {
    if (!workCenters) return sessions.length;
    return sessions.filter((s) => workCenters.includes(s.workCenter)).length;
  };

  // Get work center color for left border
  const getWorkCenterColor = (workCenter) => {
    const colors = {
      "PA": "border-l-purple-500",
      "PRINTING": "border-l-purple-500",
      "PM": "border-l-pink-500",
      "ASSEMBLY": "border-l-teal-500",
      "OPTI": "border-l-teal-500",
      "3D MODEL": "border-l-orange-500",
      "PAINT": "border-l-orange-500",
      "FULFILLMNT": "border-l-blue-500",
      "QA": "border-l-cyan-500",
    };
    return colors[workCenter] || "border-l-gray-500";
  };

  // Get status indicator
  const getStatusDot = (state) => {
    if (state === "running") return "bg-green-500";
    if (state === "paused") return "bg-yellow-500";
    return "bg-slate-200";
  };

  // Calculate elapsed time (supports both session objects and startTime strings for backwards compat)
  const getElapsedTime = (sessionOrStartTime) => {
    let diff = 0;

    if (typeof sessionOrStartTime === "object" && sessionOrStartTime !== null) {
      // Session object passed - use accumulated seconds (floor to remove decimals)
      const session = sessionOrStartTime;
      const accumulated = Math.floor(session.accumulatedSeconds || 0);

      if (session.state === "running" && session.startTime) {
        const start = new Date(session.startTime);
        const currentSegment = Math.floor((currentTime - start) / 1000);
        diff = accumulated + (currentSegment > 0 ? currentSegment : 0);
      } else if (session.durationSeconds) {
        diff = Math.floor(session.durationSeconds);
      } else {
        diff = accumulated;
      }
    } else {
      // Legacy: just startTime string passed
      const start = new Date(sessionOrStartTime);
      diff = Math.floor((currentTime - start) / 1000);
    }

    const hours = Math.floor(diff / 3600);
    const mins = Math.floor((diff % 3600) / 60);
    const secs = diff % 60;
    if (hours > 0) {
      return `${hours}:${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
    }
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Get total elapsed for a group of sessions
  const getTotalElapsed = (sessionList) => {
    const totalSecs = sessionList.reduce((acc, s) => {
      // Floor accumulated to remove decimals from database
      const accumulated = Math.floor(s.accumulatedSeconds || 0);
      if (s.state === "running" && s.startTime) {
        const start = new Date(s.startTime);
        const currentSegment = Math.floor((currentTime - start) / 1000);
        return acc + accumulated + (currentSegment > 0 ? currentSegment : 0);
      } else if (s.durationSeconds) {
        return acc + Math.floor(s.durationSeconds);
      }
      return acc + accumulated;
    }, 0);
    const hours = Math.floor(totalSecs / 3600);
    const mins = Math.floor((totalSecs % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  // Filter sessions by work center group and search query
  const filteredSessions = useMemo(() => {
    let filtered = sessions;

    // Filter by work center group
    if (workCenterFilter !== "all") {
      const centers = workCenterGroups[workCenterFilter] || [];
      filtered = filtered.filter((s) => centers.includes(s.workCenter));
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter((s) =>
        (s.jobNumber || "").toLowerCase().includes(q) ||
        (s.jobName || "").toLowerCase().includes(q) ||
        (s.projectName || "").toLowerCase().includes(q) ||
        (s.quoteNumber || "").toLowerCase().includes(q) ||
        (s.operationCode || "").toLowerCase().includes(q) ||
        (s.workCenter || "").toLowerCase().includes(q) ||
        getEmployeeNameById(s.employeeId).toLowerCase().includes(q)
      );
    }

    return filtered;
  }, [sessions, workCenterFilter, searchQuery]);

  // Helper to get summary of quotes/projects/operations for collapsed view
  const getGroupSummary = useCallback((sessions) => {
    const quotes = [...new Set(sessions.map(s => s.quoteNumber).filter(Boolean))];
    const projects = [...new Set(sessions.map(s => s.projectName || s.jobName).filter(Boolean))];
    const operations = [...new Set(sessions.map(s => s.operationCode).filter(Boolean))];

    const quoteSummary = quotes.length > 2
      ? `${quotes.slice(0, 2).join(", ")} +${quotes.length - 2}`
      : quotes.join(", ");

    const projectSummary = projects.length > 2
      ? `${projects.slice(0, 2).join(", ")} +${projects.length - 2} more`
      : projects.join(", ");

    const opSummary = operations.length > 3
      ? `${operations.slice(0, 3).join(", ")} +${operations.length - 3}`
      : operations.join(", ");

    return { quotes: quoteSummary, projects: projectSummary, operations: opSummary };
  }, []);

  // Group sessions based on groupBy selection
  const groupedData = useMemo(() => {
    if (groupBy === "flat") {
      // Sort by elapsed time (longest first)
      return [{
        key: "all",
        label: `All Sessions (${filteredSessions.length})`,
        sessions: [...filteredSessions].sort((a, b) =>
          new Date(a.startTime) - new Date(b.startTime)
        ),
      }];
    }

    const groups = {};

    filteredSessions.forEach((session) => {
      let key, label;

      switch (groupBy) {
        case "project":
          key = session.quoteNumber || "no-quote";
          label = session.quoteNumber
            ? `${session.quoteNumber}: ${session.projectName || session.jobName || "Unknown Project"}`
            : "No Quote Assigned";
          break;
        case "employee":
          key = session.employeeId;
          label = getEmployeeNameById(session.employeeId);
          break;
        case "workCenter":
          key = session.workCenter || "unknown";
          label = session.workCenter || "Unknown Work Center";
          break;
        default:
          key = "all";
          label = "All";
      }

      if (!groups[key]) {
        groups[key] = { key, label, sessions: [] };
      }
      groups[key].sessions.push(session);
    });

    // Sort groups by session count (most active first)
    return Object.values(groups).sort((a, b) => b.sessions.length - a.sessions.length);
  }, [filteredSessions, groupBy]);

  // Initialize collapsed state for employee view - all collapsed by default
  useEffect(() => {
    if (groupBy === "employee" && groupedData.length > 0) {
      const allCollapsed = {};
      groupedData.forEach(group => {
        // Only set collapsed if not already set (preserve user's expand/collapse actions)
        if (collapsedGroups[group.key] === undefined) {
          allCollapsed[group.key] = true;
        }
      });
      if (Object.keys(allCollapsed).length > 0) {
        setCollapsedGroups(prev => ({ ...prev, ...allCollapsed }));
      }
    }
  }, [groupBy, groupedData.length]);

  // Toggle group collapse
  const toggleGroup = useCallback((key) => {
    setCollapsedGroups(prev => ({ ...prev, [key]: !prev[key] }));
  }, []);

  // Handle row click - show details
  const handleRowClick = useCallback((type, data) => {
    setSelectedItem({ type, data });
  }, []);

  // Count sessions by work center for stats
  const workCenterCounts = useMemo(() => {
    const counts = {};
    sessions.forEach(s => {
      counts[s.workCenter] = (counts[s.workCenter] || 0) + 1;
    });
    return counts;
  }, [sessions]);

  // Stats
  const runningCount = sessions.filter(s => s.state === "running").length;
  const pausedCount = sessions.filter(s => s.state === "paused").length;

  return (
    <div className="flex-1 bg-slate-100 text-gray-900 flex flex-col overflow-auto">
      {/* STATS BAR */}
      <div className="bg-slate-100 border-b border-gray-200 py-2 px-4">
        <div className="max-w-7xl mx-auto flex items-center gap-6 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-green-500"></div>
            <span>Running: <strong>{runningCount}</strong></span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
            <span>Paused: <strong>{pausedCount}</strong></span>
          </div>
          <div className="border-l border-gray-300 h-4 mx-2"></div>
          {Object.entries(workCenterCounts).slice(0, 5).map(([wc, count]) => (
            <span key={wc} className="text-gray-500">
              {wc}: <strong className="text-gray-900">{count}</strong>
            </span>
          ))}
        </div>
      </div>

      {/* WORK CENTER GROUP BUTTONS */}
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="max-w-7xl mx-auto">
          {/* Work Center Filter Buttons - Main Row */}
          <div className="flex items-center gap-2 flex-wrap mb-3">
            {workCenterGroupsOrdered.map((group) => {
              const count = getGroupCount(group.workCenters);
              const isActive = workCenterFilter === group.key;
              return (
                <button
                  key={group.key}
                  onClick={() => setWorkCenterFilter(group.key)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    isActive
                      ? "bg-gradient-to-r from-purple-600 to-pink-500 text-gray-900 shadow-lg"
                      : "bg-slate-100 text-gray-700 hover:bg-slate-100 hover:text-gray-900"
                  }`}
                >
                  {group.label}
                  <span className={`ml-2 px-1.5 py-0.5 rounded text-xs ${
                    isActive ? "bg-slate-100" : "bg-gray-200"
                  }`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Secondary Controls Row */}
          <div className="flex items-center gap-4 flex-wrap">
            {/* Group By Dropdown */}
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-500">Group by:</label>
              <select
                value={groupBy}
                onChange={(e) => {
                  setGroupBy(e.target.value);
                  setCollapsedGroups({});
                  setSelectedItem(null);
                }}
                className="bg-slate-100 border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="project">Project</option>
                <option value="employee">Employee</option>
                <option value="workCenter">Work Center</option>
                <option value="flat">Flat List</option>
              </select>
            </div>

            {/* Search */}
            <div className="flex-1 max-w-xs">
              <input
                type="text"
                placeholder="Search jobs, employees, quotes..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-slate-100 border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>

            {/* Clear filters */}
            {(workCenterFilter !== "all" || searchQuery) && (
              <button
                onClick={() => {
                  setWorkCenterFilter("all");
                  setSearchQuery("");
                }}
                className="text-sm text-purple-600 hover:text-purple-600"
              >
                Clear filters
              </button>
            )}
          </div>
        </div>
      </div>

      {/* MAIN CONTENT */}
      <main className="flex-1 flex overflow-hidden">
        {/* LEFT: Grouped List */}
        <div className="flex-1 overflow-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-gray-500">Loading...</div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-red-400">{error}</div>
            </div>
          ) : filteredSessions.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64">
              <div className="text-gray-500 text-lg">No active sessions</div>
              <div className="text-gray-500 text-sm mt-2">
                {searchQuery ? "Try adjusting your search" : "Everyone's on break or hasn't started yet"}
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {groupedData.map((group) => (
                <div key={group.key} className="bg-slate-100 rounded-lg overflow-hidden">
                  {/* Group Header */}
                  {groupBy !== "flat" && (
                    <button
                      onClick={() => toggleGroup(group.key)}
                      className="w-full flex items-center justify-between px-4 py-2 hover:bg-slate-100/50 transition-colors"
                    >
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className={`h-4 w-4 transition-transform flex-shrink-0 ${collapsedGroups[group.key] ? "" : "rotate-90"}`}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                        <span className="font-medium flex-shrink-0">{group.label}</span>
                        <span className="text-gray-500 text-sm flex-shrink-0">({group.sessions.length})</span>

                        {/* Show quote/project/operation summary when collapsed (employee view only) */}
                        {collapsedGroups[group.key] && groupBy === "employee" && (
                          <div className="flex items-center gap-2 text-xs text-gray-500 truncate ml-2">
                            {(() => {
                              const summary = getGroupSummary(group.sessions);
                              return (
                                <>
                                  {summary.quotes && (
                                    <span className="text-cyan-600/80 truncate max-w-[120px]" title={summary.quotes}>
                                      {summary.quotes}
                                    </span>
                                  )}
                                  {summary.quotes && summary.projects && (
                                    <span className="text-gray-600">•</span>
                                  )}
                                  {summary.projects && (
                                    <span className="truncate max-w-[200px]" title={summary.projects}>
                                      {summary.projects}
                                    </span>
                                  )}
                                  {summary.projects && summary.operations && (
                                    <span className="text-gray-600">•</span>
                                  )}
                                  {summary.operations && (
                                    <span className="text-purple-600/70 truncate max-w-[150px]" title={summary.operations}>
                                      {summary.operations}
                                    </span>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-500 flex-shrink-0">
                        {/* Only show total elapsed when NOT grouping by employee (since jobs may overlap) */}
                        {groupBy !== "employee" && (
                          <span>{getTotalElapsed(group.sessions)} total</span>
                        )}
                        <div className="flex items-center gap-1">
                          {group.sessions.filter(s => s.state === "running").length > 0 && (
                            <span className="flex items-center gap-1">
                              <span className="w-2 h-2 rounded-full bg-green-500"></span>
                              {group.sessions.filter(s => s.state === "running").length}
                            </span>
                          )}
                          {group.sessions.filter(s => s.state === "paused").length > 0 && (
                            <span className="flex items-center gap-1 ml-2">
                              <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
                              {group.sessions.filter(s => s.state === "paused").length}
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  )}

                  {/* Sessions List */}
                  {(!collapsedGroups[group.key] || groupBy === "flat") && (
                    <div className="divide-y divide-gray-200/50">
                      {group.sessions.map((session) => (
                        <button
                          key={session.id}
                          onClick={() => handleRowClick("session", session)}
                          className={`w-full flex items-center gap-3 px-4 py-2 hover:bg-slate-100/30 transition-colors text-left border-l-4 ${getWorkCenterColor(session.workCenter)} ${
                            selectedItem?.type === "session" && selectedItem?.data?.id === session.id
                              ? "bg-purple-900/30"
                              : ""
                          }`}
                        >
                          {/* Status dot */}
                          <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${getStatusDot(session.state)}`}></div>

                          {/* Employee name - show if not grouping by employee */}
                          {groupBy !== "employee" && (
                            <span className="w-28 truncate text-sm font-medium">
                              {getEmployeeNameById(session.employeeId)}
                            </span>
                          )}

                          {/* Quote # + Project Name - show when grouping by employee */}
                          {groupBy === "employee" && (
                            <span className="w-48 truncate text-sm">
                              {session.quoteNumber && (
                                <span className="text-cyan-600 font-mono">{session.quoteNumber}</span>
                              )}
                              {session.quoteNumber && (session.projectName || session.jobName) && (
                                <span className="text-gray-500 mx-1">·</span>
                              )}
                              <span className="text-gray-700">{session.projectName || session.jobName || ""}</span>
                            </span>
                          )}

                          {/* Job Number */}
                          <span className="w-24 truncate font-mono text-sm text-purple-600">
                            {session.jobNumber}
                          </span>

                          {/* Operation/Work Center */}
                          <span className="w-20 truncate text-xs text-gray-500">
                            {session.operationCode}
                          </span>
                          <span className="w-20 truncate text-xs px-1.5 py-0.5 bg-gray-200 rounded">
                            {session.workCenter}
                          </span>

                          {/* Quote - show if not grouping by project or employee */}
                          {groupBy !== "project" && groupBy !== "employee" && session.quoteNumber && (
                            <span className="w-24 truncate text-xs text-gray-500">
                              {session.quoteNumber}
                            </span>
                          )}

                          {/* Elapsed Time */}
                          <span className={`ml-auto font-mono text-sm ${
                            session.state === "running" ? "text-green-400" : "text-yellow-400"
                          }`}>
                            {getElapsedTime(session)}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* RIGHT: Detail Panel */}
        {selectedItem && (
          <div className="w-[400px] border-l border-gray-200 bg-white overflow-auto flex-shrink-0">
            <div className="p-4">
              {/* Close button */}
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Details</h3>
                <button
                  onClick={() => setSelectedItem(null)}
                  className="text-gray-500 hover:text-gray-900"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {selectedItem.type === "session" && (
                <SessionDetailPanel
                  session={selectedItem.data}
                  getElapsedTime={getElapsedTime}
                  getStatusDot={getStatusDot}
                  getWorkCenterColor={getWorkCenterColor}
                  isAdmin={isAdmin}
                  deleteConfirm={deleteConfirm}
                  setDeleteConfirm={setDeleteConfirm}
                  handleDeleteTicket={handleDeleteTicket}
                  deleting={deleting}
                />
              )}
            </div>
          </div>
        )}
      </main>

      {/* FOOTER */}
      <footer className="bg-white border-t border-gray-200 px-4 py-2">
        <div className="max-w-7xl mx-auto flex items-center justify-between text-sm text-gray-500">
          <span>Auto-refreshes every 10 seconds</span>
          <span>Last updated: {currentTime.toLocaleTimeString()}</span>
        </div>
      </footer>
    </div>
  );
}

/**
 * Detail panel for a selected session
 */
function SessionDetailPanel({
  session,
  getElapsedTime,
  getStatusDot,
  getWorkCenterColor,
  isAdmin,
  deleteConfirm,
  setDeleteConfirm,
  handleDeleteTicket,
  deleting,
}) {
  return (
    <div className="space-y-4">
      {/* Status Header */}
      <div className={`flex items-center gap-3 p-3 rounded-lg ${
        session.state === "running" ? "bg-green-50" : "bg-yellow-900/30"
      }`}>
        <div className={`w-4 h-4 rounded-full ${getStatusDot(session.state)} ${
          session.state === "running" ? "animate-pulse" : ""
        }`}></div>
        <span className="font-medium">
          {session.state === "running" ? "Currently Working" : "Paused"}
        </span>
        <span className={`ml-auto text-2xl font-mono font-bold ${
          session.state === "running" ? "text-green-400" : "text-yellow-400"
        }`}>
          {getElapsedTime(session)}
        </span>
      </div>

      {/* Employee */}
      <div>
        <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Employee</div>
        <div className="text-lg font-semibold">{getEmployeeNameById(session.employeeId)}</div>
      </div>

      {/* Job Info */}
      <div className={`p-3 bg-slate-100 rounded-lg border-l-4 ${getWorkCenterColor(session.workCenter)}`}>
        <div className="font-mono text-purple-600 text-lg">{session.jobNumber}</div>
        <div className="text-gray-700 mt-1">{session.jobName || "—"}</div>
        {session.projectName && session.projectName !== session.jobName && (
          <div className="text-sm text-gray-500 mt-1">{session.projectName}</div>
        )}
      </div>

      {/* Operation & Work Center */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Operation</div>
          <div className="px-2 py-1 bg-slate-100 rounded text-sm inline-block">{session.operationCode}</div>
        </div>
        <div>
          <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Work Center</div>
          <div className="px-2 py-1 bg-slate-100 rounded text-sm inline-block">{session.workCenter}</div>
        </div>
      </div>

      {/* Quote/Order */}
      {(session.quoteNumber || session.orderNumber) && (
        <div className="grid grid-cols-2 gap-4">
          {session.quoteNumber && (
            <div>
              <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Quote</div>
              <div className="text-sm">{session.quoteNumber}</div>
            </div>
          )}
          {session.orderNumber && (
            <div>
              <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Order</div>
              <div className="text-sm">{session.orderNumber}</div>
            </div>
          )}
        </div>
      )}

      {/* Printer (if assigned) */}
      {session.printer && (
        <div>
          <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Printer</div>
          <div className="px-2 py-1 bg-purple-100 text-purple-600 rounded text-sm inline-block">
            {session.printer}
          </div>
        </div>
      )}

      {/* Quantity */}
      {session.qty && (
        <div>
          <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Quantity</div>
          <div className="text-sm">{session.qty}</div>
        </div>
      )}

      {/* Start Time */}
      <div>
        <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Started</div>
        <div className="text-sm">{new Date(session.startTime).toLocaleString()}</div>
      </div>

      {/* Admin Delete Section */}
      {isAdmin && (
        <div className="pt-4 mt-4 border-t border-gray-200">
          {deleteConfirm === session.id ? (
            <div className="bg-red-900/30 border border-red-700 rounded-lg p-3">
              <p className="text-red-600 text-sm mb-3">
                Are you sure you want to delete this ticket? This cannot be undone.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => handleDeleteTicket(session.id)}
                  disabled={deleting}
                  className="flex-1 px-3 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 rounded text-sm font-medium transition-colors"
                >
                  {deleting ? "Deleting..." : "Yes, Delete"}
                </button>
                <button
                  onClick={() => setDeleteConfirm(null)}
                  disabled={deleting}
                  className="flex-1 px-3 py-2 bg-gray-200 hover:bg-gray-200 disabled:opacity-50 rounded text-sm font-medium transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setDeleteConfirm(session.id)}
              className="w-full px-3 py-2 bg-red-100 hover:bg-red-900/80 border border-red-800 rounded text-red-400 text-sm font-medium transition-colors flex items-center justify-center gap-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Delete Ticket
            </button>
          )}
        </div>
      )}
    </div>
  );
}
